import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import {
  Menu,
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  MessageCircle,
  AlertTriangle,
} from "lucide-react";

const Header = ({ onMenuClick, user }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const { logout, isAdmin } = useAuth();
  const { whatsappConfigured } = useData(); // Destructure whatsappConfigured
  const navigate = useNavigate();

  useEffect(() => {
    // ... existing click outside logic
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ... handlers
  const handleProfileClick = () => {
    setUserMenuOpen(false);
    navigate("/profile");
  };

  const handleSettingsClick = () => {
    setUserMenuOpen(false);
    navigate("/settings");
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    window.location.href = "/login";
  };

  return (
    <>
      {isAdmin && whatsappConfigured === false && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
           <div className="flex items-center gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              <span>WhatsApp integration is not configured. Connect your WhatsApp Business account to enable messaging features.</span>
           </div>
           <button 
             onClick={() => navigate('/settings')}
             className="text-xs font-semibold text-amber-700 hover:text-amber-900 bg-amber-100 px-3 py-1 rounded-full transition-colors"
           >
             Connect Now
           </button>
        </div>
      )}
      <header className="px-4 py-3 bg-white border-b border-gray-200 shadow-sm ">
        <div className="flex items-center justify-between">
            {/* Left side - Menu button and title */}
            <div className="flex items-center space-x-4">
              <button
                onClick={onMenuClick}
                className="p-2 text-gray-600 rounded-md lg:hidden hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <Menu className="w-5 h-5" />
              </button>

          <div className="hidden lg:flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-700">Management Platform</span>
          </div>
        </div>

        {/* Right side - Notifications and User menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative p-2 text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute flex items-center justify-center w-4 h-4 text-xs text-white rounded-full -top-1 -right-1 bg-danger-500">
                3
              </span>
            </button>

            {/* Notifications dropdown */}
            {notificationsOpen && (
              <div className="absolute right-0 z-50 mt-2 bg-white border border-gray-200 rounded-md shadow-lg w-80">
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-900">
                      Notifications
                    </h3>
                  </div>
                  <div className="overflow-y-auto max-h-64">
                    {/* Sample notifications */}
                    <div className="px-4 py-3 cursor-pointer hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <MessageCircle className="h-5 w-5 text-primary-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            New message from Admin
                          </p>
                          <p className="text-xs text-gray-500">2 minutes ago</p>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-3 cursor-pointer hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <MessageCircle className="h-5 w-5 text-success-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">
                            Exam results published
                          </p>
                          <p className="text-xs text-gray-500">1 hour ago</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-2 border-t border-gray-100">
                    <button className="text-sm font-medium text-primary-600 hover:text-primary-700">
                      View all notifications
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center p-2 space-x-2 text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="hidden text-left md:block">
                <p className="text-sm font-medium text-gray-900">
                  Profile
                </p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.name}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && (
              <div className="absolute right-0 z-50 w-48 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="py-1">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <User className="w-4 h-4 mr-3" />
                    Profile
                  </button>
                  {isAdmin && (
                    <button
                      onClick={handleSettingsClick}
                      className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Settings
                    </button>
                  )}
                  <div className="my-1 border-t border-gray-100"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  </>
  );
};

Header.propTypes = {
  onMenuClick: PropTypes.func.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
  }),
};

Header.defaultProps = {
  user: null,
};

export default Header;
